<template>
  <div v-if="otjsReady" id="programme-otj-logs">
    <k-panel :hasContentToggle="false">
      <template #body>
        <div class="panel-heading">
          <div class="header-left">
            <span>Total Time Spent:
              <span v-if="filteredOtjs.length > 0" class="total-time-spent">
                {{ formatTimeSpent(totalTimeSpent) }}<br>
                <small><i>(Only includes approved O.T.J.s)</i></small>
              </span>
            <span v-else>0h</span>
            </span>
          </div>
          <div v-if="!store.isDashboard" class="header-right">
            <k-dropdown-multi-select
              v-if="otjs.length > 0"
              v-model="selectedStatuses"
              :options="otjStatuses"
              :showSearch="false"
              placeholder="Filter by Status"
            />
            <button class="btn btn-success" @click="openNewOtjModal">
              <i class="fas fa-edit"></i> Add new O.T.J.
            </button>
          </div>
        </div>
        <k-table v-if="otjs.length > 0"
          :headers="store.isDashboard ? dashboardKableHeaders : kableHeaders"
          :rows="kableRows"
          :hideable="false"
          :panel="false"
          :max="10"
          :enableTopScroll="true"
          @clicked="handleTableAction"
        ></k-table>
        <empty-placeholder v-else
          title="No O.T.J. Logs Found"
          info="There are no O.T.J. logs for this programme yet. Click the 'Add new O.T.J.' button to get started."
        ></empty-placeholder>
      </template>
    </k-panel>
    <k-modal :show="showCoachNotesModal" @close="showCoachNotesModal = false">
      <template #header>
        <h3>Why was it rejected?</h3>
      </template>
      <template #body>
        <h4>Coach's Notes</h4>
        <p class="coach-notes-text">{{ coachNotes }}</p>
      </template>
      <template #footer>
        <button class="btn btn-primary" @click="showCoachNotesModal = false">Close</button>
      </template>
    </k-modal>
    <k-modal class="learning-description-modal" :show="showLearningDescriptionModal" @close="showLearningDescriptionModal = false">
        <template #header>
            <h3>Learning Description</h3>
        </template>
        <template #body>
            <p class="learning-description-full-text">{{ currentLearningDescription }}</p>
        </template>
        <template #footer>
            <button class="btn btn-primary" @click="showLearningDescriptionModal = false">Close</button>
        </template>
    </k-modal>
    <add-edit-otj v-if="canEditOtj"
      :show-modal="showOtjModal"
      @close="handleModalClose"
      @update="getOtjLogs"
      :programme-id="programmeId"
      :learner-id="userId"
      :programme-member-id="programmeMemberId"
      :is-editing="isEditing"
      :otj-data="otjToEdit"
      :compliance-mode="store.isDashboard && $permissions.hasPermission('manage_user_otjs')"
    ></add-edit-otj>
    <rejecting-otj-modal
      :show-modal="showRejectionModal"
      @close="showRejectionModal = false"
      :otj-data="otjToReject"
      @update-status="setOtjStatus"
      :programme-id="programmeId"
      :programme-member-id="programmeMemberId"
    ></rejecting-otj-modal>
    <otj-submission-history
      :show="showSubmissionHistoryModal"
      :otj-entry-id="resubmittedOtj.id"
      @close="showSubmissionHistoryModal = false"
    ></otj-submission-history>
  </div>
</template>

<style>
#programme-otj-logs .k-table-container th.k-table-header-open_learning_description .k-table-header-text {
  white-space: pre-wrap;
  text-overflow: unset;
}

#programme-otj-logs .k-table-container th.k-table-header-open_learning_description,
#programme-otj-logs .k-table-header-edit,
#programme-otj-logs .k-table-header-note,
#programme-otj-logs .k-table-header-delete,
#programme-otj-logs .k-table-header-approve,
#programme-otj-logs .k-table-header-reject {
  min-width: 85px;
}

#programme-otj-logs .k-table-header-edit .k-table-header-content,
#programme-otj-logs .k-table-header-note .k-table-header-content,
#programme-otj-logs .k-table-header-delete .k-table-header-content,
#programme-otj-logs .k-table-header-open_learning_description .k-table-header-content {
  justify-content: center;
}

#programme-otj-logs .k-table-key-edit,
#programme-otj-logs .k-table-key-note,
#programme-otj-logs .k-table-key-delete,
#programme-otj-logs .k-table-key-open_learning_description {
  text-align: center;
}

.status-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
  display: inline-block;
  text-transform: capitalize;
}

.status-badge.approved,
.status-badge.rejected,
.status-badge.pending {
  color: var(--kate-type-white);
}

.status-badge.approved {
  background-color: var(--kate-success);
}

.status-badge.rejected {
  background-color: var(--kate-danger);
}

.status-badge.pending {
  background-color: var(--kate-primary);
  color: var(--kate-type-dark);
}

.status-badge.draft {
  background-color: var(--kate-button-outlined-hover);
  color: var(--kate-type-dark);
}

#programme-otj .review-button {
  border: none;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

#programme-otj-logs .k-table-string .k-table-key-end_time,
#programme-otj-logs .k-table-string .k-table-key-start_time {
  text-transform: lowercase;
}

.learning-description-modal .modal-container {
  max-width: 90vw;
}

.learning-description-full-text {
  background-color: var(--kate-panel-light);
  border: var(--kate-panel-light-border);
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.5;
  margin: 0;
  border-radius: 8px;
  padding: 16px;
}
</style>

<style scoped>
.panel-heading {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  align-items: center;
  margin-bottom: 20px;
}

.header-left,
.header-right {
  display: flex;
  gap: 10px;
}

.header-left {
  align-items: center;
}

.header-right {
  align-items: stretch;
}

.vertical-hr {
  height: 20px;
  width: 1px;
  background-color: var(--kate-border);
  margin: 0 10px;
}

p.coach-notes-text {
  background-color: var(--input-background);
  padding: 10px;
  border-radius: 8px;
  border: var(--input-border-alt);
}
</style>

<script>
import KModal from '@base-components/k-modal.vue';
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/k-table.vue';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import TimeMixin from '@mixins/time-mixins';
import useGlobalStore from '@stores/global';
import { sortObjectArray } from '@utils/sort-by-object-property';
import { LEARNING_ACTIVITY_TYPES } from '@constants';
import KDropdownMultiSelect from '@base-components/k-dropdown-multi-select.vue';
import AddEditOtj from './otj_components/add-edit-otj.vue';
import RejectingOTJModal from '../dashboard/monitor/otjs/rejecting-otj-modal.vue';
import OtjSubmissionHistory from './otj-submission-history.vue';

const ACTION_MAP = {
  edit: {
    title: 'Edit O.T.J. entry',
    icon: 'fas fa-pencil-alt',
    buttonClass: 'btn btn-default',
  },
  delete: {
    title: 'Delete O.T.J. entry',
    icon: 'fas fa-trash',
    buttonClass: 'btn btn-danger',
  },
  note: {
    title: 'See reason for rejection',
    icon: 'fas fa-external-link-alt',
    buttonClass: 'btn btn-primary',
  },
  approve: {
    title: 'Approve O.T.J. entry',
    icon: 'fas fa-thumbs-up',
    buttonClass: 'btn btn-success',
  },
  reject: {
    title: 'Reject O.T.J. entry',
    icon: 'fas fa-thumbs-down',
    buttonClass: 'btn btn-outlined-danger',
  },
  open_submission_history: {
    title: 'View submission history',
    icon: 'fas fa-history',
    buttonClass: 'btn btn-primary',
  },
};

export default {
  components: {
    KModal,
    KTable,
    KPanel,
    AddEditOtj,
    EmptyPlaceholder,
    KDropdownMultiSelect,
    'rejecting-otj-modal': RejectingOTJModal,
    'otj-submission-history': OtjSubmissionHistory,
  },

  mixins: [TimeMixin],

  props: {
    dashboardProgrammeId: Number,
    userId: Number,
    programmeMemberId: Number,
  },

  data() {
    return {
      store: useGlobalStore(),
      otjs: [],
      otjsReady: false,
      showOtjModal: false,
      isEditing: false,
      otjToEdit: undefined,
      showCoachNotesModal: false,
      coachNotes: '',
      learningActivityTypes: LEARNING_ACTIVITY_TYPES,
      showLearningDescriptionModal: false,
      currentLearningDescription: '',
      showRejectionModal: false,
      otjToReject: undefined,
      selectedStatuses: [],
      totalTimeSpent: 0,
      showSubmissionHistoryModal: false,
      resubmittedOtj: {},
      otjStatuses: [
        { id: 'approved', name: 'Approved' },
        { id: 'pending', name: 'Pending' },
        { id: 'rejected', name: 'Rejected' },
        { id: 'draft', name: 'Draft' },
      ],
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getOtjLogs();
  },

  watch: {
    programmeId(val) {
      if (val) {
        this.getOtjLogs();
      }
    },
  },

  computed: {
    isApp() {
      return !this.store.isDashboard;
    },

    appProgrammeId() {
      return Number(this.$route.params.programmeId);
    },

    programmeId() {
      return this.store.isDashboard ? this.dashboardProgrammeId : this.appProgrammeId;
    },

    filteredOtjs() {
      const otjsWithoutResubmitted = this.otjs.filter(otj => !otj.has_been_resubmitted);
      if (!this.isApp || this.selectedStatuses.length === this.otjStatuses.length || this.selectedStatuses.length === 0) {
        return otjsWithoutResubmitted;
      }
      return otjsWithoutResubmitted.filter(otj => this.selectedStatuses.includes(otj.status.toLowerCase()));
    },

    kableHeaders() {
      // Removed sortable: true, // TODO: This is broken because of the formatting we put the data through
      return {
        date: {
          name: 'Date',
          type: 'string',
        },
        start_time: {
          name: 'Start',
          type: 'string',
        },
        end_time: {
          name: 'End',
          type: 'string',
        },
        time_spent: {
          name: 'Duration',
          type: 'string',
        },
        status: {
          name: 'Status',
          type: 'function',
          options: {
            callback: this.statusDisplay,
          },
        },
        learning_activity: {
          name: 'Activity',
          type: 'string',
        },
        open_learning_description: {
          name: 'Description',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        },
        competencies: {
          name: 'Competencies',
          type: 'tags',
        },
        edit: {
          name: 'Edit',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        },
        note: {
          name: 'Coach Notes',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        },
        delete: {
          name: 'Delete',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        },
        open_submission_history: {
          name: 'Submission History',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        },
      };
    },

    dashboardKableHeaders() {
      const headers = {
        date: {
          name: 'Date',
          type: 'string',
        },
        start_time: {
          name: 'Start',
          type: 'string',
        },
        end_time: {
          name: 'End',
          type: 'string',
        },
        time_spent: {
          name: 'Duration',
          type: 'string',
        },
        status: {
          name: 'Status',
          type: 'function',
          options: {
            callback: this.statusDisplay,
          },
        },
        learning_activity: {
          name: 'Activity',
          type: 'string',
        },
        description: {
          name: 'Description',
          type: 'string',
        },
        competencies: {
          name: 'Competencies',
          type: 'tags',
        },
        note: {
          name: 'Coach Notes',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        },
      };
      if (this.$permissions.hasPermission('manage_user_otjs')) {
        headers.edit = {
          name: 'Edit',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        };
      }
      if (this.store.learnerSupportType === 'coach') {
        headers.approve = {
          name: 'Approve',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        };
        headers.reject = {
          name: 'Reject',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        };
      }
      return headers;
    },

    kableRows() {
      if (!this.otjsReady) {
        return [];
      }
      if (this.store.isDashboard) {
        return sortObjectArray(this.filteredOtjs, 'date_completed', true).map(otj => {
          const row = {
            id: otj.id,
            date: this.parseTimestamp(otj.start_time, undefined, 'dd/MM/yyyy'),
            start_time: this.parseTimestampToTime(otj.start_time, undefined, 'h:mm a'),
            end_time: this.parseTimestampToTime(otj.end_time, undefined, 'h:mm a'),
            time_spent: this.formatTimeSpent(otj.time_spent),
            status: {
              text: this.statusDisplay(otj),
              sortBy: otj.status,
            },
            learning_activity: otj.category,
            description: otj.learning_description,
            competencies: {
              tags: otj.competencies ? otj.competencies.map(c => c.label) : [],
              sortBy: otj.competencies ? otj.competencies.map(c => c.label).join(',') : '',
            },
            note: {
              text: this.tableOpenCoachModalButton(otj),
            },
          };
          if (this.$permissions.hasPermission('manage_user_otjs')) {
            row.edit = {
              text: this.tableActionButtonDisplay('any', 'edit'),
            };
          }
          if (this.store.learnerSupportType === 'coach') {
            row.approve = {
              text: this.tableActionButtonDisplay(otj, 'approve'),
              disabled: otj.status.toLowerCase() !== 'pending',
            };
            row.reject = {
              text: this.tableActionButtonDisplay(otj, 'reject'),
              disabled: otj.status.toLowerCase() !== 'pending',
            };
          }
          return row;
        });
      }

      return sortObjectArray(this.filteredOtjs, 'date_completed', true).map(otj => ({
        id: otj.id,
        date: this.parseTimestamp(otj.start_time, undefined, 'dd/MM/yyyy'),
        start_time: this.parseTimestampToTime(otj.start_time, undefined, 'h:mm a'),
        end_time: this.parseTimestampToTime(otj.end_time, undefined, 'h:mm a'),
        time_spent: this.formatTimeSpent(otj.time_spent),
        status: {
          text: this.statusDisplay(otj),
          sortBy: otj.status,
        },
        learning_activity: otj.category,
        open_learning_description: {
          text: '<button class="btn btn-primary" title="View Description"><i class="fas fa-eye"></i></button>',
        },
        competencies: {
          tags: otj.competencies ? otj.competencies.map(c => c.label) : [],
          sortBy: otj.competencies ? otj.competencies.map(c => c.label).join(',') : '',
        },
        edit: {
          text: this.tableActionButtonDisplay(otj, 'edit'),
          disabled: otj.status.toLowerCase() === 'pending' || otj.status.toLowerCase() === 'approved' || (otj.status.toLowerCase() === 'rejected' && otj.has_been_resubmitted === true),
        },
        note: {
          text: this.tableOpenCoachModalButton(otj),
        },
        delete: {
          text: this.tableActionButtonDisplay(otj, 'delete'),
          disabled: otj.status.toLowerCase() !== 'draft',
        },
        open_submission_history: {
          text: this.tableActionButtonDisplay(otj, 'open_submission_history'),
        },
      }));
    },

    canEditOtj() {
      if (this.store.isDashboard) {
        // On dashboard can only edit someone else's OTJ if you have the required permission
        return this.$permissions.hasPermission('manage_user_otjs');
      }
      // On app, you're editing your own OTJ
      return true;
    },
  },

  methods: {
    getAppOtjLogs() {
      this.$Loading.start();
      this.otjsReady = false; // Set to false before fetching to show loading state
      this.$logger.info('Fetching OTJ logs', { programmeId: this.appProgrammeId });
      this.$http.get(`/api/curriculum/programmes/${this.appProgrammeId}/otj-entries`)
        .then(response => {
          this.$logger.info('Successfully fetched OTJ logs', { programmeId: this.appProgrammeId });
          this.otjs = response.data.otj_entries || [];
          this.totalTimeSpent = response.data.total_time_spent;
        })
        .catch(err => {
          this.$logger.autowarn('Error fetching OTJ logs', { programmeId: this.appProgrammeId }, err);
          this.$ktoast.warning('Error fetching OTJ logs');
          this.otjs = [];
        }).then(() => {
          this.otjsReady = true;
          this.$Loading.finish();
        });
    },

    getDashboardOtjLogs() {
      this.$Loading.start();
      this.otjsReady = false; // Set to false before fetching to show loading state
      this.$logger.info('Fetching OTJ logs', { programmeId: this.dashboardProgrammeId });
      this.$http.get(`/api/curriculum/admin/programmes/${this.dashboardProgrammeId}/otj-entries/${this.userId}`)
        .then(response => {
          this.$logger.info('Successfully fetched OTJ logs', { programmeId: this.dashboardProgrammeId });
          this.otjs = response.data.otj_entries || [];
          this.totalTimeSpent = response.data.total_time_spent;
        })
        .catch(err => {
          this.$logger.autowarn('Error fetching OTJ logs', { programmeId: this.dashboardProgrammeId }, err);
          this.$ktoast.warning('Error fetching OTJ logs');
          this.otjs = [];
        }).then(() => {
          this.otjsReady = true;
          this.$Loading.finish();
        });
    },

    getOtjLogs() {
      if (this.store.isDashboard) {
        this.getDashboardOtjLogs();
      } else {
        this.getAppOtjLogs();
      }
    },

    getCoachNotes(otjId) {
      this.$Loading.start();
      this.$http.get(`/api/curriculum/programmes/${this.programmeId}/otj-entries/${otjId}/feedback`)
        .then(response => {
          this.coachNotes = response.data.feedback[0].otj_comment; // Select the latest feedback entry
          this.$Loading.finish();
        })
        .catch(err => {
          this.$logger.autowarn('Error fetching coach notes', err);
          this.$ktoast.warning('Error fetching coach notes');
          this.$Loading.finish();
        });
    },

    deleteOtj(otjId) {
      this.$logger.info('Deleting OTJ', { id: otjId });
      this.$Loading.start();
      this.$http.delete(`/api/curriculum/programmes/${this.programmeId}/otj-entries/${otjId}`)
        .then(() => {
          this.$logger.info('OTJ deleted', { id: otjId });
          this.$ktoast.success('OTJ deleted');
          this.getOtjLogs();
        })
        .catch(err => {
          this.$logger.autowarn('Error deleting OTJ', { id: otjId }, err);
          this.$ktoast.warning('Error deleting OTJ');
        }).then(() => {
          this.$Loading.finish();
        });
    },

    handleTableAction(key, index, row, column) {
      if (column === 'delete') {
        this.confirmDeleteOtj(row.id);
      } else if (column === 'edit') {
        this.otjToEdit = this.otjs.find(x => x.id === row.id);
        this.openEditOtjModal();
      } else if (column === 'note') {
        this.getCoachNotes(row.id);
        this.showCoachNotesModal = true;
      } else if (column === 'open_learning_description') {
        this.openLearningDescriptionModal(row.id);
      } else if (column === 'approve') {
        this.setOtjStatus(row.id, true);
      } else if (column === 'reject') {
        this.rejectOtj(row.id);
      } else if (column === 'open_submission_history') {
        this.openSubmissionHistoryModal(row.id);
      }
    },

    confirmDeleteOtj(key) {
      this.$ktoast.confirmToast('Are you sure you want to delete this OTJ?', 'warning', () => {
        this.deleteOtj(key);
      });
    },

    statusDisplay(row) {
      const status = typeof row === 'object' && row.status?.text ? row.status.sortBy : row.status;
      const statusClass = status.toLowerCase();
      const capitalizedStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
      return `<div class="status-cell"><span class="status-badge ${statusClass}">${capitalizedStatus}</span></div>`;
    },

    tableActionButtonDisplay(otj, action) {
      const status = otj.status?.toLowerCase() || otj.toLowerCase(); // Handle cases where otj might be a string
      let buttonTitle = ACTION_MAP[action].title;
      const buttonIcon = ACTION_MAP[action].icon;
      let buttonClass = ACTION_MAP[action].buttonClass;
      let disabledAttr = '';
      if (action === 'open_submission_history') {
        return `<button class="${buttonClass}" aria-label="${buttonTitle}" title="${buttonTitle}"><i class="${buttonIcon}"></i></button>`;
      }
      if (this.isApp) {
        if (action === 'edit' && status === 'rejected') {
          // On app, rejected entry edit button is purple (primary)
          buttonClass = 'btn btn-primary';
        }
      } else if (action === 'edit') {
        disabledAttr = status === 'approved' ? 'disabled' : '';
      }
      if (this.isApp) {
        switch (status) {
          case 'rejected':
            buttonTitle = action === 'edit' ? 'Edit rejected entry for re-submission' : 'Delete rejected entry';
            if (typeof otj === 'object' && otj.has_been_resubmitted) {
              disabledAttr = 'disabled';
            } else {
              disabledAttr = action === 'edit' ? '' : 'disabled';
            }
            break;
          case 'draft':
            buttonTitle = action === 'edit' ? 'Edit draft entry' : 'Delete draft entry';
            break;
          default:
            disabledAttr = 'disabled';
            break;
        }
      } else if (['approve', 'reject'].includes(action) && status !== 'pending') {
        disabledAttr = 'disabled';
      }
      return `<button class="${buttonClass}" aria-label="${buttonTitle}" title="${buttonTitle}" ${disabledAttr}><i class="${buttonIcon}"></i></button>`;
    },

    tableOpenCoachModalButton(row) {
      const disabledAttr = row.status.toLowerCase() === 'rejected' ? '' : 'disabled';
      return `<button class="${ACTION_MAP.note.buttonClass}" aria-label="${ACTION_MAP.note.title}" title="${ACTION_MAP.note.title}" ${disabledAttr}><i class="${ACTION_MAP.note.icon}"></i></button>`;
    },

    formatTimeSpent(minutes) {
      const minutesValue = minutes ?? 0; // Ensure minutes is not undefined
      const hours = Math.floor(minutesValue / 60);
      const remainingMinutes = minutesValue % 60;
      if (hours === 0) {
        return `${remainingMinutes}m`;
      }
      if (remainingMinutes === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${remainingMinutes}m`;
    },

    openNewOtjModal() {
      this.otjToEdit = undefined;
      this.showOtjModal = true;
      this.isEditing = false;
    },

    openEditOtjModal() {
      this.showOtjModal = true;
      this.isEditing = true;
    },

    handleModalClose() {
      this.showOtjModal = false;
    },

    openLearningDescriptionModal(otjId) {
      const foundOtj = this.otjs?.find(otj => otj?.id === otjId);
      if (!foundOtj) {
        return;
      }
      this.currentLearningDescription = foundOtj.learning_description;
      this.showLearningDescriptionModal = true;
    },

    openSubmissionHistoryModal(otjId) {
      const foundOtj = this.otjs?.find(otj => otj?.id === otjId);
      if (!foundOtj) {
        return;
      }
      this.resubmittedOtj = foundOtj;
      this.showSubmissionHistoryModal = true;
    },

    setOtjStatus(otjId, value) {
      const otj = this.otjs?.find(o => o.id === otjId);
      if (!otj || !this.programmeId) {
        return;
      }
      const jsonPayload = {
        status: value ? 'approved' : 'rejected',
        programme_competency_ids: otj.competencies ? otj.competencies.map(c => c.id) : [],
        date_completed: this.formatDate(otj.date_completed),
        time_spent: otj.time_spent,
        category: otj.category,
        learning_description: otj.learning_description,
        declared_in_working_hours: otj.declared_in_working_hours,
        programme_member_id: this.programmeMemberId,
        user_id: this.userId,
        start_time: this.formatDate(otj.start_time),
        end_time: this.formatDate(otj.end_time),
      };

      this.$logger.info('Setting OTJ status', { otjId }, true);
      this.$http.put(`/api/curriculum/admin/programmes/${this.programmeId}/otj-entries/${otjId}`, jsonPayload)
        .then(() => {
          this.$logger.info('OTJ status set', { otjId });
          this.getOtjLogs();
          this.showOtjModal = false;
        })
        .catch(err => {
          this.$logger.error('Error setting OTJ status', { otjId }, err);
          this.$ktoast.showError(err, true);
        });
    },

    rejectOtj(otjId) {
      this.otjToReject = this.otjs?.find(otj => otj?.id === otjId);
      if (!this.otjToReject) {
        return;
      }
      this.showRejectionModal = true;
    },
  },
};
</script>
